import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import {translate} from 'iss/strings';
import styles from 'iss/styles/SearchableSelector.styl';
import {toArray} from 'iss/utils/functional';
import {Button} from 'iss/widgets/Buttons';
import {FlexItem, FlexRow} from 'iss/widgets/Flex';
import {selectorFormComponent} from 'iss/widgets/Forms';
import {PagedSearchResults, SearchResultScroller, TextSearchBox, formatResultCount} from 'iss/widgets/PagedSearchResults';
import BootstrapFormGroup from 'iss/widgets/bootstrap/FormGroup';

/** Displays a selector showing a list of items that can be searched selected
 * Props:
 *
 * getItems(searchStr, pageNr, PageSize) :: string -> number -> number -> SearchResult | Observable<SearchResult>
 * The function used to fetch the items to show
 * The SearchResult object should have the fields results :: Array<object>, totalResults :: number
 *
 * children: a function that takes an item and returns
 *           its rendered representation in the selector
 *
 * selectedItemFormatter: a function that takes an item (and optionally it's
 *                        index) and returns its rendered representation for
 *                        the list of selected items
**/
export const SearchableSelector = selectorFormComponent(class SearchableSelector extends React.Component {
  static propTypes = {
    getItems: PropTypes.func.isRequired,
    children: PropTypes.func.isRequired,
    selectedIndicator: PropTypes.func,
  };
  static defaultProps = {
    selectedItemFormatter: (item) => item.name,
    debounced: false,
  };
  state = { search: "" };
  updateSearch = (search) => {
    this.setState({search});
  };
  render() {
    const {id, multi, debounced, value, onChange, isChosen, onToggle, children, getItems, selectedItemFormatter} = this.props;
    const ResultToolbar = multi ? MultiResultToolbar : ResultCount;
    const {search} = this.state;

    return <BootstrapFormGroup {...this.props}>
      <TextSearchBox id={id+"-search"} search={search} onChange={this.updateSearch} />
      <PagedSearchResults search={search} getItems={getItems} debounced={debounced}>
        {(result, searchApi) =>
          <React.Fragment>
            <ResultToolbar result={result} setSelection={onChange} showAll={searchApi.loadAllItems}/>
            <SelectorPanel id={id} searchResult={result}
              loadMore={searchApi.loadMoreItems}
              isChosen={isChosen} onToggle={onToggle}>
              {children}
            </SelectorPanel>
          </React.Fragment>
        }
      </PagedSearchResults>
      <SelectedIndicator selected={value} itemFormatter={selectedItemFormatter} />
    </BootstrapFormGroup>;
  }
});

function ResultCount({result}) {
  return <p>{formatResultCount(result)}</p>;
}

function MultiResultToolbar({result, setSelection, showAll}) {
  const totalItems = result.totalResults;
  const hasMoreItems = totalItems > result.results.length;

  return <FlexRow className={styles.search}>
    <FlexItem alignItems="center" minHeight="3em">
      {formatResultCount(result)}
      {" "}
      {hasMoreItems && totalItems < 200 && <Button onClick={showAll} text={translate('common.showAllResults')} />}
    </FlexItem>
    <FlexItem flex="none">
      {!hasMoreItems && <Button onClick={() => setSelection(result.results)} text={translate('common.selectAll')} />}
      <Button onClick={() => setSelection([])} text={translate('common.clearSelection')} />
    </FlexItem>
  </FlexRow>;
}

export function SelectorPanel({id, loadMore, searchResult, children, isChosen, onToggle}) {
  return <SearchResultScroller className={styles.selector+" panel panel-default"} id={id} searchResult={searchResult} onLoadMore={loadMore} hasMaxHeight={true}>
    {searchResult.results.length > 0
      ? searchResult.results.map(item =>
        <SelectorItem key={item.id} id={id+"-"+item.id} itemType={id}
          onToggle={() => onToggle(item)} isChosen={isChosen(item)}>
          {children(item)}
        </SelectorItem>
      ) : <div className={styles.noItems}>{translate('common.noItemsFound')}</div>
    }
  </SearchResultScroller>;
}

function SelectorItem({id, children, onToggle, isChosen, itemType}) {
  const classes = classnames(
    styles.item, `${itemType}-selector`,
    {[styles.selected]: isChosen}
  );
  return <div id={id} className={classes} onClick={onToggle}>
    {children}
  </div>;
}

function SelectedIndicator({selected, itemFormatter}) {
  //if (!selected || selected.length == 0) return null;
  const items = toArray(selected);
  const multi = items.length > 1;
  const Elem = multi ? "div" : React.Fragment;

  return <React.Fragment>
    {items.length == 1 ?
        translate('common.selected') + ": " :
        <p>{translate('common.selectedCount', {count: items.length})}</p>
    }
    {itemFormatter ? items.map((item, index) =>
      <Elem key={item.id}>{itemFormatter(item, index)} </Elem>
    ) : null}
  </React.Fragment>;
}



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/selectors/SearchableSelector.jsx